import { FC, useCallback, useState } from 'react';
import { sortObjectArray, SortOrder } from 'utils/array';
import { RolloutGroup, RolloutProgress } from 'models/rollout';
import Table from 'components/UI/Table';
import TableHeader from 'components/UI/Table/TableHeader';
import TableRow from 'components/UI/Table/TableRow';
import TableHeaderCell from 'components/UI/Table/TableHeaderCell';
import { FormattedMessage } from 'react-intl';
import TableBody from 'components/UI/Table/TableBody';
import TableBodyCell from 'components/UI/Table/TableBodyCell';
import TableDataCell from 'components/UI/Table/TableDataCell';
import ArrowIcon from 'components/UI/Icon/ArrowIcon';
import * as routes from 'router/Routes';
import { useNavigate } from 'react-router';
import Progressbar from 'components/UI/Progressbar';

const headerCells = [
  {
    title: 'rollout.group_id',
    sortable: true,
  },
  {
    title: 'rollout.progress',
    sortable: false,
  },
  {
    title: 'rollout.status',
    sortable: true,
  },
];

export type TableGroupInRollout = {
  groupName: string;
  progress: RolloutProgress;
  status: string;
  link: string;
};

type RollouGroupTableProps = {
  groups: RolloutGroup[];
  rolloutId: string;
  initialActiveColumn: number;
  initialSortOrder: SortOrder;
};

const RolloutGroupTable: FC<RollouGroupTableProps> = ({
  groups,
  rolloutId,
  initialActiveColumn,
  initialSortOrder,
}) => {
  // State
  const navigate = useNavigate();
  const [sortOrder] = useState<SortOrder>(initialSortOrder);
  const [activeColumnIndex] = useState<number>(initialActiveColumn);

  // Fetch device and navigate
  const onClick = useCallback(
    (groupId: string) => {
      if (groupId) {
        navigate(`${routes.ROLLOUTS}/${rolloutId}/groups/${groupId}`);
      }      
    },
    [navigate, rolloutId]
  );

  const table: TableGroupInRollout[] = groups.map((group: RolloutGroup) => {
    const rolloutTableItem = {
      groupName: group.name || '–',
      progress: group.progress,
      status: group.status || '–',
      link: group.id.toString() || '–',
    };
    return rolloutTableItem;
  });

  // Sorted
  const sortedGroups: TableGroupInRollout[] = sortObjectArray(
    table,
    Object.keys(table[0])[activeColumnIndex],
    sortOrder
  ) as TableGroupInRollout[];

  // Render arrow button
  const renderDetailsButton = useCallback(
    (link: string) => {
      if (!link) {
        return null;
      }
      return (
        <TableDataCell onClick={() => onClick(link)} isPressable>
          <ArrowIcon
            size="small"
            direction="right"
            transition={false}
            themeType="grey6"
          />
        </TableDataCell>
      );
    },
    [onClick]
  );

  return (
    <div>
      <Table>
        <TableHeader>
          <TableRow>
            {headerCells.map((cell, index) => {
              return (
                <TableHeaderCell key={index}>
                  {cell.title && <FormattedMessage id={cell.title} />}
                </TableHeaderCell>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedGroups.map((item, rowIndex) => {
            return (
              <TableRow key={rowIndex}>
                {Object.entries(item).map(([name, value], colIndex) => {
                  if (name === 'progress') {
                    return (
                      <TableDataCell
                        key={`progressbar-{rowIndex}`}
                        displayType={'flow'}
                      >
                        <Progressbar
                          succeeded={(value as RolloutProgress).succeeded}
                          failed={(value as RolloutProgress).failed}
                          waiting={(value as RolloutProgress).waiting}
                          notStarted={(value as RolloutProgress).notStarted}
                          cancelled={(value as RolloutProgress).cancelled}
                          scheduled={(value as RolloutProgress).scheduled}
                        />
                      </TableDataCell>
                    );
                  }
                  return (
                    <TableBodyCell key={colIndex} name={name} value={value} />
                  );
                })}
                {renderDetailsButton(item.link)}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default RolloutGroupTable;
